import apiClient from './apiClient';

// ============================================================
// Types -- matching backend Go models (c212/models/c212.go)
// ============================================================

export interface FC210 {
  fc210_01: string;          // Forecast No (PK)
  fc210_11: string | null;   // Order date
  fc210_12: string;          // Customer code (下單客戶)
  fc210_13: string;          // Garment buyer (成衣買主)
  fc210_14: string;          // Sales person code (業務人員)
  fc210_15: string;          // Department code
  fc210_16: string;          // Currency code
  fc210_17: number;          // Exchange rate
  fc210_18: string;          // Created by user
  fc210_19: string | null;   // Reserved date
  fc210_20: string;          // Product usage code (產品用途)
  fc210_21: string;          // Garment buyer code (成衣買主代碼)
  fc210_22: number;          // Accumulated inbound qty
  fc210_23: number;          // Detail total count
  fc210_24: string;          // Purchase person code
  fc210_25: number;          // Purchase unit price
  fc210_26: number;          // Purchase qty
  fc210_27: string;          // Purchase person code2
  fc210_28: string | null;   // Purchase date
  fc210_29: string;          // Purchase unit
  fc210_30: string;          // Purchase memo
  fc210_31: number;          // Reserved int
  fc210_32: string;          // Memo
  fc210_33: number;          // Attachment count
  fc210_79: string;          // Soft-delete flag
  fc210_80: string;          // Approval status (N/P)
  fc210_81: string;          // Approval user
  fc210_811: string | null;  // Approval date
  fc210_812: string;         // Approval time
  fc210_91: string;          // Modified by
  fc210_92: string | null;   // Modified at
  fc210_93: string;          // Created by

  // Associations
  details?: FC211[];
  attachments?: FC213[];
}

export interface FC211 {
  fc211_01: string;          // Forecast No (FK)
  fc211_02: number;          // Sequence no
  fc211_10: string;          // Product code part1
  fc211_11: string;          // Product code part2
  fc211_12: number;          // Forecast qty
  fc211_13: string | null;   // Customer required date
  fc211_14: string;          // Unit code
}

export interface FC213 {
  fc213_01: string;          // Forecast No (FK)
  fc213_02: number;          // Sequence no
  fc213_11: string;          // Attachment name
  fc213_12: string;          // Local file path
  fc213_21: number;          // File type
  fc213_22: string;          // Upload status
  fc213_91: string;          // Modified by
  fc213_92: string | null;   // Modified at
  fc213_93: string;          // Created by
}

// ============================================================
// Request / Response types
// ============================================================

export interface C212ListResponse {
  data: FC210[];
  total: number;
  page: number;
  size: number;
}

export interface C212CreateRequest {
  master: Partial<FC210>;
  details: Partial<FC211>[];
  attachments: Partial<FC213>[];
}

export interface ValidationError {
  field: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const c212Api = {
  async list(page = 1, size = 20): Promise<C212ListResponse> {
    const res = await apiClient.get('/api/c212', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FC210> {
    const res = await apiClient.get(`/api/c212/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: C212CreateRequest): Promise<FC210> {
    const res = await apiClient.post('/api/c212', data);
    return res.data;
  },

  async update(id: string, data: C212CreateRequest): Promise<FC210> {
    const res = await apiClient.put(`/api/c212/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/c212/${encodeURIComponent(id)}`);
  },

  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/c212/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },

  // Approval workflow
  async submitForApproval(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/c212/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/c212/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/c212/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/c212/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<unknown[]> {
    const res = await apiClient.get(`/api/c212/${encodeURIComponent(id)}/approval-history`);
    return res.data;
  },
};
